import React from "react";
import { motion } from "framer-motion";
import { TrendingDown } from "lucide-react";

const lineItems = [
  {
    name: "Premium communication suite",
    example: "e.g. Weave, RevenueWell",
    price: "$750 – $1,200",
  },
  {
    name: "Practice analytics",
    example: "e.g. Dental Intel",
    price: "$300 – $500",
  },
  {
    name: "Insurance verification software",
    example: "e.g. Zuub, AirPay",
    price: "$300 – $500",
  },
  {
    name: "AI Receptionist",
    example: "e.g. Dentina, Arini",
    price: "$499",
  },
  {
    name: "AI Hygiene Recall",
    example: "e.g. Patient Desk",
    price: "$1,000",
  },
  {
    name: "AI Case Acceptance",
    example: "e.g. Codent AI",
    price: "$500",
  },
  {
    name: "eClaims portal",
    example: "e.g. DentalXChange, Vyne Trellis",
    price: "$200 – $250",
  },
  {
    name: "AI Schedule Optimizer",
    example: "e.g. Patient Desk",
    price: "$1,000",
  },
];

const parseRange = (price) => {
  const nums = price.replace(/\$|,/g, "").split("–").map((s) => parseInt(s.trim(), 10));
  return nums.length === 2 ? nums : [nums[0], nums[0]];
};

const totals = lineItems.reduce(
  (acc, item) => {
    const [lo, hi] = parseRange(item.price);
    return { lo: acc.lo + lo, hi: acc.hi + hi };
  },
  { lo: 0, hi: 0 },
);

const fmt = (n) => `$${n.toLocaleString()}`;

const FrontOfficePricingComparisonSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex items-stretch p-5 xl:p-8 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs — blue scheme */}
      <div
        className="absolute top-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex gap-10 relative z-10 h-full max-w-[1240px] mx-auto items-center">
        {/* Left side — title */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-[420px] flex-shrink-0 flex flex-col justify-center"
        >
          <h2 className="text-[40px] lg:text-[46px] font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-8">
            Front Office{" "}
            <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
              Pricing Comparison
            </span>
          </h2>
          <div className="space-y-5">
            <p className="text-[16px] text-slate-600 leading-relaxed font-medium">
              These are actual vendors' published rates for the front office
              software components alone — no AI back office included.
            </p>
            <div className="flex gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="w-8 h-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center shadow-sm shadow-blue-500/20">
                  <TrendingDown className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <p className="text-[16px] text-slate-600 leading-relaxed font-medium">
                Asha bundles every one of these — plus the entire back office —
                for{" "}
                <span className="text-blue-600 font-bold">
                  a fraction of the cost.
                </span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Right side — line-item table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex-1 rounded-[24px] overflow-hidden shadow-[0_20px_80px_-20px_rgba(0,0,0,0.12)] border border-slate-200 bg-white flex flex-col h-[640px] relative"
        >
          {/* Header */}
          <div className="grid grid-cols-[1fr_180px] border-b border-slate-200 flex-shrink-0 bg-white z-20 relative shadow-sm">
            <div className="px-6 py-4 flex items-end">
              <span className="text-xs font-black uppercase tracking-widest text-slate-400">
                Front office stack
              </span>
            </div>
            <div className="px-4 py-4 text-right border-l border-slate-100">
              <span className="text-xs font-black uppercase tracking-widest text-slate-400">
                Monthly cost
              </span>
            </div>
          </div>

          {/* Rows */}
          <div className="flex-1 bg-white overflow-hidden flex flex-col">
            {lineItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.04, duration: 0.3 }}
                className="grid grid-cols-[1fr_180px] border-b border-slate-100 hover:bg-slate-50/70 transition-colors flex-1 min-h-0"
              >
                <div className="px-6 py-2.5 flex flex-col justify-center">
                  <span className="text-[14px] font-semibold text-slate-900 leading-tight">
                    {item.name}
                  </span>
                  <span className="text-[11.5px] text-slate-400 italic mt-0.5">
                    {item.example}
                  </span>
                </div>
                <div className="px-4 py-2.5 flex items-center justify-end border-l border-slate-100">
                  <span className="text-[14px] font-bold text-slate-800 whitespace-nowrap">
                    {item.price}
                    <span className="text-[11px] font-semibold text-slate-400">
                      {" "}
                      /mo
                    </span>
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Total bar */}
          <div className="grid grid-cols-[1fr_180px] border-t-2 border-slate-200 bg-slate-50 flex-shrink-0 z-20 relative shadow-[0_-10px_20px_rgba(0,0,0,0.03)]">
            <div className="px-6 py-3.5 flex flex-col justify-center">
              <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                Total — front office only
              </span>
              <span className="text-[11px] text-slate-400 italic mt-0.5">
                No AI back office included
              </span>
            </div>
            <div className="px-4 py-3.5 flex items-center justify-end border-l border-slate-200">
              <span className="text-[19px] font-black text-slate-900 whitespace-nowrap">
                {fmt(totals.lo)}–{fmt(totals.hi)}
                <span className="text-[11px] font-semibold text-slate-400">
                  {" "}
                  /mo
                </span>
              </span>
            </div>
          </div>

          {/* Asha comparison bar */}
          <div className="grid grid-cols-[1fr_180px] border-t border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50/60 flex-shrink-0 z-20 relative">
            <div className="px-6 py-3.5 flex flex-col justify-center">
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-sm uppercase tracking-widest w-fit">
                  Asha OS
                </div>
                <span className="text-[13px] font-bold text-blue-700">
                  Full front + back office
                </span>
              </div>
              <span className="text-[11px] text-slate-500 italic mt-1">
                Continuous Integration pricing
              </span>
            </div>
            <div className="px-4 py-3.5 flex items-center justify-end border-l border-blue-100">
              <span className="text-[19px] font-black text-blue-700 whitespace-nowrap">
                $800
                <span className="text-[11px] font-semibold text-blue-400">
                  {" "}
                  /mo
                </span>
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FrontOfficePricingComparisonSlide;
